import { useCallback, useEffect, useState } from 'react';
import { Trans } from '@lingui/macro';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    makeStyles,
    Typography,
} from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { includes, toLower } from 'lodash';
import { CancelOutlined, CheckCircleOutline } from '@material-ui/icons';
import clsx from 'clsx';

import NgpFileUpload from '../../form/NgpFileUpload';
import { ColumnFlexLayout, RowFlexLayout } from '../../layout';
import { useDataService } from '../../../services/ApiDataService';

const UPLOAD_URL = '/ipm/v1/admin/systemconfiguration/uploadSystemUpgrade';
const STATUS_URL = '/ipm/v1/admin/systemconfiguration/systemUpgradeStatus';

const STATE_SELECT = 0;
const STATE_UPLOADING = 1;
const STATE_UPGRADING = 2;
const STATE_SUCCESS = 3;
const STATE_ERROR = 4;

export default function SystemUpgradeDialog(props) {
    const { open, setOpen } = props;
    const methods = useForm();
    const { handleSubmit } = methods;

    const [state, setState] = useState(STATE_SELECT);
    const [stateStatus, setStateStatus] = useState();
    const dataService = useDataService();

    const hasUploaded = state === STATE_UPGRADING;

    const onError = useCallback((status) => {
        setState(STATE_ERROR);
        setStateStatus(status);
    }, []);

    const onSuccess = useCallback((status) => {
        setState(STATE_SUCCESS);
        setStateStatus(status);
    }, []);

    const { status: upgradeStatus } = useSystemUpgradeStatus({
        hasUploaded,
        onError,
        onSuccess,
    });

    const beginUpgrade = async (data) => {
        if (state !== STATE_SELECT) return;

        const formData = new FormData();
        const { upgradeFile, checksumFile } = data;
        formData.append('upgradeFile', upgradeFile);
        formData.append('checksumFile', checksumFile);

        try {
            setState(STATE_UPLOADING);
            await dataService.postFormData(UPLOAD_URL, formData, null, {
                timeout: 1000 * 60 * 60 * 6,
            });
            setState(STATE_UPGRADING);
        } catch (e) {
            setStateStatus(`File upload failed: ${e.message}`);
            setState(STATE_ERROR);
        }
    };

    return (
        <FormProvider {...methods}>
            <Dialog open={open}>
                {state === STATE_SELECT && (
                    <DialogTitle>
                        <Trans>Upgrade System</Trans>
                    </DialogTitle>
                )}

                <DialogContent>
                    <Box width={475}>
                        {state === STATE_SELECT && <FileUploadSection />}

                        {includes([STATE_UPLOADING, STATE_UPGRADING], state) && (
                            <UpgradeStatusSection hasUploaded={hasUploaded} status={upgradeStatus} />
                        )}

                        {includes([STATE_SUCCESS, STATE_ERROR], state) && (
                            <CompletedSection success={state === STATE_SUCCESS} status={stateStatus} />
                        )}
                    </Box>
                </DialogContent>

                <DialogActions>
                    {state === STATE_SELECT && (
                        <>
                            <Button onClick={handleSubmit(beginUpgrade)}>
                                <Trans>Begin Upgrade</Trans>
                            </Button>
                            <Button onClick={() => setOpen(false)}>
                                <Trans>Close</Trans>
                            </Button>
                        </>
                    )}

                    {state === STATE_ERROR && (
                        <Button onClick={() => setOpen(false)}>
                            <Trans>Close</Trans>
                        </Button>
                    )}

                    {state === STATE_SUCCESS && (
                        // eslint-disable-next-line no-restricted-globals
                        <Button onClick={() => location.reload()}>
                            <Trans>Reload Window</Trans>
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </FormProvider>
    );
}

function FileUploadSection() {
    return (
        <ColumnFlexLayout hStretch px={4} py={0.5}>
            <NgpFileUpload
                fieldName="upgradeFile"
                label="Upgrade Package (.img)"
                maxFileSize={10e9 * 10} // 10gb should be enough...
                formSubmit
                required
                requiredFileType="img"
                buttonProps={{
                    size: 'large',
                }}
            />
            <NgpFileUpload
                fieldName="checksumFile"
                label="SHA-256 Checksum (.sha-256)"
                formSubmit
                helpText={
                    <Typography variant="caption">
                        <Trans>Note: SHA-256 checksum is optional</Trans>
                    </Typography>
                }
                buttonProps={{
                    size: 'large',
                }}
            />
        </ColumnFlexLayout>
    );
}

function UpgradeStatusSection(props) {
    const { hasUploaded, status } = props;

    return (
        <div>
            <Typography variant="subtitle2">
                {hasUploaded ? (
                    <Trans>Upgrading System...</Trans>
                ) : (
                    <Trans>Sending upgrade package to server. This may take a while.</Trans>
                )}
            </Typography>
            <LinearProgress variant="indeterminate" />
            <Typography component={Box} pt={1} variant="caption">
                {status}
            </Typography>
        </div>
    );
}

export function useSystemUpgradeDialog() {
    const [open, setOpen] = useState(false);

    const show = useCallback(() => setOpen(true), []);

    return {
        show,
        dialog: open && <SystemUpgradeDialog open setOpen={setOpen} />,
    };
}

function useSystemUpgradeStatus(options) {
    const { hasUploaded, onError, onSuccess } = options;

    const dataService = useDataService();
    const [status, setStatus] = useState();

    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (!hasUploaded || success !== null) return;
        loadStatus();
        const reloadTask = setInterval(loadStatus, 5000);
        return () => clearInterval(reloadTask);

        async function loadStatus() {
            try {
                const { status: serverStatus } = await dataService.get(STATUS_URL);
                setStatus(serverStatus);

                const statusLower = toLower(serverStatus);

                if (statusLower.indexOf('upgrade to version') === 0) {
                    setSuccess(true);
                    onSuccess(serverStatus);
                } else if (statusLower.indexOf('error:') !== -1) {
                    setSuccess(false);
                    onError(serverStatus);
                }
            } catch (e) {
                // do nothing, network failures are expected on upgrade as services are restarting
            }
        }
    }, [dataService, hasUploaded, onError, onSuccess, success]);

    return { status };
}

const useCompletedSectionStyles = makeStyles((theme) => ({
    success: {
        '& .MuiSvgIcon-root': {
            color: theme.palette.success.main,
        },
    },

    failure: {
        '& .MuiSvgIcon-root': {
            color: theme.palette.error.main,
        },
    },

    titleText: {
        ...theme.typography.h6,
    },
}));

function CompletedSection(props) {
    const { status, success } = props;
    const classes = useCompletedSectionStyles();

    return (
        <ColumnFlexLayout>
            <RowFlexLayout
                vCenter
                className={clsx({
                    [classes.success]: success,
                    [classes.failure]: !success,
                })}
            >
                {success ? (
                    <CheckCircleOutline size="large" color="inherit" />
                ) : (
                    <CancelOutlined size="large" color="inherit" />
                )}
                <Box ml={1} className={classes.titleText}>
                    {success ? <Trans>Upgrade Successful</Trans> : <Trans>Upgrade Failed</Trans>}
                </Box>
            </RowFlexLayout>

            {!!status && (
                <Box mt={2} component={Typography} variant="subtitle2">
                    {status}
                </Box>
            )}
        </ColumnFlexLayout>
    );
}
